import { useState } from "react";
import TextField from "../components/TextField";
import {
  createContactFromRawText,
  createBulkContactFromJson
} from "../api/contacts";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { FiUserPlus, FiFileText, FiUpload } from "react-icons/fi";

export default function CreateContact() {
  const [activeTab, setActiveTab] = useState("manual");
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  // 🔹 Manual Form State
  const [manualForm, setManualForm] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    category: "",
  });

  // 🔹 Raw Text / JSON State
  const [rawText, setRawText] = useState("");

  // 🔹 File Upload State
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    batchesProcessed: 0,
    totalCreated: 0,
    totalSkipped: 0,
  });

  // Handle Manual Form Input
  const handleManualChange = (e) => {
    setManualForm({ ...manualForm, [e.target.id]: e.target.value });
  };

  // 🔹 Handle Manual Single Contact Submit
  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualForm.email.trim()) {
      toast.error("Email address is required!");
      return;
    }
    try {
      setLoading(true);
      const res = await createBulkContactFromJson(token.access, [manualForm]);
      if (res.created_count > 0) {
        toast.success(`Contact '${manualForm.name || manualForm.email}' added successfully! 🎉`);
        setManualForm({ name: "", email: "", phone: "", company_name: "", category: "" });
      } else {
        toast.error("Contact with this email already exists!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add contact");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle Raw Text Submit
  const handleRawSubmit = async (e) => {
    e.preventDefault();
    if (!rawText.trim()) {
      toast.error("Please paste some text or JSON");
      return;
    }

    // Try parsing as JSON first
    try {
      const parsedJson = JSON.parse(rawText);
      if (Array.isArray(parsedJson)) {
        setLoading(true);
        const res = await createBulkContactFromJson(token.access, parsedJson);
        toast.success(`✅ Created: ${res.created_count}, Skipped: ${res.skipped_count}`);
        setRawText("");
        return;
      }
    } catch (e) {
      // Not JSON, fallback to raw text parsing
    }

    try {
      setLoading(true);
      await createContactFromRawText(token.access, rawText);
      toast.success("Contact(s) imported successfully!");
      setRawText("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to import contacts");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Handle File Upload (JSON or CSV)
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadProgress({ batchesProcessed: 0, totalCreated: 0, totalSkipped: 0 });

      const text = await file.text();
      let contacts = [];

      if (file.name.endsWith(".json")) {
        contacts = JSON.parse(text);
      } else if (file.name.endsWith(".csv")) {
        // Simple CSV parser
        const lines = text.split("\n").filter((l) => l.trim());
        if (lines.length > 1) {
          const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
          const nameIdx = headers.findIndex((h) => h.includes("name"));
          const emailIdx = headers.findIndex((h) => h.includes("email"));
          const phoneIdx = headers.findIndex((h) => h.includes("phone"));
          const companyIdx = headers.findIndex((h) => h.includes("company"));
          const catIdx = headers.findIndex((h) => h.includes("category"));

          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(",").map((c) => c.trim().replace(/^"(.*)"$/, "$1"));
            if (cols[emailIdx >= 0 ? emailIdx : 0]) {
              contacts.push({
                name: nameIdx >= 0 ? cols[nameIdx] : "",
                email: emailIdx >= 0 ? cols[emailIdx] : cols[0],
                phone: phoneIdx >= 0 ? cols[phoneIdx] : "",
                company_name: companyIdx >= 0 ? cols[companyIdx] : "",
                category: catIdx >= 0 ? cols[catIdx] : "",
              });
            }
          }
        }
      }

      if (!Array.isArray(contacts) || contacts.length === 0) {
        throw new Error("No valid contacts found in file");
      }

      // Batch processing (100 contacts per batch)
      const batchSize = 100;
      let batchIndex = 0;

      while (batchIndex < contacts.length) {
        const batch = contacts.slice(batchIndex, batchIndex + batchSize);
        const res = await createBulkContactFromJson(token.access, batch);

        setUploadProgress((prev) => ({
          batchesProcessed: prev.batchesProcessed + 1,
          totalCreated: prev.totalCreated + res.created_count,
          totalSkipped: prev.totalSkipped + res.skipped_count,
        }));

        batchIndex += batchSize;
      }

      toast.success("✅ File imported successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to process file");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <FiUserPlus /> Add Contacts
      </h1>

      {/* Simplified Tabs */}
      <div className="flex border-b border-gray-200 mb-6 bg-white p-1 rounded-t-lg">
        <button
          onClick={() => setActiveTab("manual")}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-t-lg transition ${
            activeTab === "manual"
              ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50/50"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <FiUserPlus /> Single Contact Form
        </button>
        <button
          onClick={() => setActiveTab("file")}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-t-lg transition ${
            activeTab === "file"
              ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50/50"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <FiUpload /> Upload CSV / JSON File
        </button>
        <button
          onClick={() => setActiveTab("raw")}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-t-lg transition ${
            activeTab === "raw"
              ? "border-b-2 border-blue-600 text-blue-600 bg-blue-50/50"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <FiFileText /> Paste Raw Text / JSON
        </button>
      </div>

      {/* Tab 1: Manual Single Contact Form */}
      {activeTab === "manual" && (
        <form onSubmit={handleManualSubmit} className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          <TextField
            id="name"
            label="Full Name"
            value={manualForm.name}
            onChange={handleManualChange}
            placeholder="John Doe"
          />
          <TextField
            id="email"
            label="Email Address *"
            type="email"
            value={manualForm.email}
            onChange={handleManualChange}
            placeholder="john@example.com"
            required
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              id="phone"
              label="Phone Number"
              value={manualForm.phone}
              onChange={handleManualChange}
              placeholder="+123456789"
            />
            <TextField
              id="company_name"
              label="Company Name"
              value={manualForm.company_name}
              onChange={handleManualChange}
              placeholder="Acme Corp"
            />
          </div>
          <TextField
            id="category"
            label="Category / Tag"
            value={manualForm.category}
            onChange={handleManualChange}
            placeholder="VIP, Clients, Leads..."
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white font-medium py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Saving Contact..." : "Save Contact"}
          </button>
        </form>
      )}

      {/* Tab 2: Upload CSV / JSON File */}
      {activeTab === "file" && (
        <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          <p className="text-sm text-gray-600">
            Select a <strong>.csv</strong> or <strong>.json</strong> file from your computer to import multiple contacts automatically.
          </p>
          <div className="border-2 border-dashed border-gray-300 p-8 rounded-lg text-center hover:border-blue-500 transition">
            <input
              type="file"
              accept=".json,.csv"
              onChange={handleFileUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
            />
          </div>
          {uploading && (
            <div className="p-4 bg-blue-50 text-blue-900 rounded-md text-sm">
              ⏳ Importing... Batches processed: {uploadProgress.batchesProcessed} | Total Created: {uploadProgress.totalCreated} | Skipped: {uploadProgress.totalSkipped}
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Paste Raw Text / JSON */}
      {activeTab === "raw" && (
        <form onSubmit={handleRawSubmit} className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          <TextField
            id="rawText"
            label="Paste Raw Text or JSON Array"
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste text like: John Doe john@example.com +123456789 or a JSON array"
            multiline
            rows={8}
            required
          />
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white font-medium py-2.5 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? "Processing Text..." : "Import Contacts"}
          </button>
        </form>
      )}
    </div>
  );
}

